import { useNavigate, useParams } from "react-router-dom";
import { useGetJobsJobsGet } from "../../../api/generated/fastAPI";
import { useEffect, useState, MouseEvent } from "react";
import { AirflowJob } from "./JobQueue";
import { customInstance } from "../../../api/custom-instance";
import SinglePageHeader from "../../../components/Page/SinglePageHeader";
import Dropdown, { DropdownOption } from "../../../components/Menus&Dropdowns/Dropdown";
import JobDetails from "../components/JobDetails";
import GanttChart from "../components/GanttChart";
import FlowDiagram from "../components/FlowDiagram";
import { ReactFlowProvider } from "@xyflow/react";
import { ReactComponent as TaskIcon } from '../../../assets/Icons/adornments/Task.svg';

import useMessage from "../../../hooks/useMessage";
import SnackBar from "../../../components/Alerts/SnackBar";
import DeleteModal from "../../../components/Popups/DeleteModal";
import useDelete from "../../../hooks/endpoints/useDelete";
import { Skeleton } from "@mui/material";
import { formatDisplayedJobMetadata, MetadataMenuItemType } from "../../../utils/dataFormattingFns";
import UpdatedMetadataMenu from "../../../components/Menus&Dropdowns/UpdatedMetadataMenu";


export interface JobTask {
    dag_id: string;
    duration: number;
    end_date: string;
    map_index: number;
    max_tries: number;
    run_id: string;
    start_date: string;
    state: string;
    task_id: string;
    try_number: number;
}

export interface IndividualAirflowJob extends AirflowJob {
    tasks: JobTask[];
    dag_final_state_report?: {
        dag_state: string;
        report: {
            dag_state: string;
            all_created_resources: any;
            task_info: {
                [key: string]: {
                    created_resources: any,
                    state: string,
                    upstream_tasks: string[],
                };
            }
        }
    }
}

export default function IndividualJob() {
    // dag run id
    const { id } = useParams();
    const getJobMutation = useGetJobsJobsGet({
        dag_run_id: id ? Number(id) : 0
    });
    const [ job, setJob ] = useState<IndividualAirflowJob | null>(null);
    const [ tasks, setTasks ] = useState<any[]>([]);
    const [ jobMetadata, setJobMetadata ] = useState<MetadataMenuItemType[]>([]);

    const navigate = useNavigate();
    const [ isTasksLoading, setIsTasksLoading ] = useState(false);
    const [ selectedRun, setSelectedRun ] = useState<{
        dag_id: string,
        task_id?: string,
        map_index?: number
    } | null>(null);

    const {
        open,
        setOpen,
        message,
        setPopupMessage,
        isSuccess,
    } = useMessage();

    const { deleteJobs } = useDelete({ setPopupMessage });


    useEffect(() => {
        if (getJobMutation.data) {
            setJob(getJobMutation.data as unknown as IndividualAirflowJob);
        };

    }, [getJobMutation.data]);

    useEffect(() => {
        const fetchJobMetadata = async (airflowJob: IndividualAirflowJob) => {
            const metadata = await formatDisplayedJobMetadata(airflowJob);
            setJobMetadata(metadata);
        }

        if (job) {
            fetchJobMetadata(job);
        }
    }, [job]);

    useEffect(() => {
        if (job) {

            setSelectedRun({
                dag_id: job.dag_id,
            });

            const allTasks = job.tasks;

            const getTaskInfo = async (jobTasks: JobTask[]) => {
                let updatedTasks: any[] = [];
                const promises = jobTasks.map(async(task) => {
                    const url = `jobs/task?dag_id=${encodeURIComponent(task.dag_id)}&task_id=${encodeURIComponent(task.task_id)}&run_id=${encodeURIComponent(task.run_id)}&map_index=${encodeURIComponent(task.map_index)}&include_xcom=true`;
                    return customInstance({
                        method: 'GET',
                        url,
                    })
                    .then(response => updatedTasks.push(response))
                });
                
                setIsTasksLoading(false);

                try {
                    await Promise.all(promises);
                    setTasks(updatedTasks);
                } catch (err:any) {
                    console.log(err);
                }

                setIsTasksLoading(false);

            };

            if (allTasks.length > 0) {
                getTaskInfo(allTasks);
            }
        }
    }, [job]);

    const dropdownOptions: DropdownOption[] = [
        {
            label: 'Delete',
            btnComp: (
                <DeleteModal 
                    items={[{
                        type: 'job',
                        id: job ? { dag_id: job.dag_id, run_id: job.run_id } : '',
                        name: job ? job.dag_id : '',
                    }]}
                    deleteFn={deleteJobs}
                />
            ),
        }
    ];



    return (
        <div className="h-full flex flex-col justify-start items-stretch gap-4 p-10">
            <SinglePageHeader 
                id='single-job-header' 
                label={job ? job.dag_id : ''} 
                status={job ? job.dag_final_state_report && job.dag_final_state_report.dag_state ? job.dag_final_state_report.dag_state : job.state : ''}
                isLoading={isTasksLoading || getJobMutation.isLoading} 
                pages={[
                    {
                        label: 'Job Queue',
                        action: (e: MouseEvent<HTMLButtonElement>) => {navigate('/jobs')}
                    },
                    {
                        label: job ? job.dag_id : '',
                    }
                ]}
                itemMetadata={tasks.length > 0 ? [{
                    label: 'Tasks',
                    value: tasks.length.toString(),
                    adornment: <TaskIcon />
                }] : []}
            >
                <Dropdown
                    btn={{
                        label: 'Options',
                        type: 'primary',
                        styling: 'solid',
                        id: 'job-options'
                    }}
                    options={dropdownOptions}
                />
                {jobMetadata.length > 0 && (
                    <UpdatedMetadataMenu 
                        menuLabel="Job Metadata"
                        menuItems={jobMetadata}
                        location="page"
                    />
                )}
            </SinglePageHeader>

            <>
            {(isTasksLoading || getJobMutation.isLoading) ? (
                <>
                <div className="w-full h-[700px] flex justify-start items-start gap-8">
                    <div className='w-1/2 h-full flex flex-col justify-start items-stretch gap-2'>
                        <div className='w-full h-[30px]'><Skeleton variant='rounded' width='100%' height='100%'/></div>
                        <div className='w-full grow h-full'><Skeleton variant='rounded' width='100%' height='100%'/></div>
                    </div>
                    <div className='w-1/2 h-full flex flex-col justify-start items-stretch gap-2'>
                        <div className='w-full h-[30px]'><Skeleton variant='rounded' width='100%' height='100%'/></div>
                        <div className='w-full grow h-full'><Skeleton variant='rounded' width='100%' height='100%'/></div>
                    </div>
                </div>
                <div className='w-full h-[700px] flex flex-col justify-start items-stretch gap-2'>
                    <div className='w-full h-[30px]'><Skeleton variant='rounded' width='100%' height='100%'/></div>
                    <div className='w-full grow h-full'><Skeleton variant='rounded' width='100%' height='100%'/></div>
                </div>
                </>
            ) : (
                <>
                    <div className="w-full h-full flex justify-start items-start gap-8">
                        <GanttChart 
                            job={job}
                            tasks={tasks}
                            setSelectedRun={setSelectedRun}
                        />
                        {selectedRun && (
                            <JobDetails 
                                job={job}
                                tasks={tasks}
                                selectedRun={selectedRun}
                            />
                        )}
                    </div>

                    {job && job.dag_final_state_report && Object.keys(job.dag_final_state_report).length > 0 && (
                    <ReactFlowProvider>
                        <FlowDiagram 
                            job={job}
                            tasks={tasks}
                        />
                    </ReactFlowProvider>
                    )}
                </>
            )}
            </>

            <SnackBar open={open} setOpen={setOpen} message={message} isSuccess={isSuccess}/>  
            
        </div>
    )
}